import { StoreMap } from '../stores/StoreMap';
import type { StoreItem, StorePageConfig } from '../stores/types';

export const StoresController = {
    index(): void {
        const configEl = document.getElementById('store-data');
        if (!configEl) return;

        let config: StorePageConfig;
        try {
            config = JSON.parse(configEl.textContent || '{}') as StorePageConfig;
        } catch {
            return;
        }

        const stores: StoreItem[] = Array.isArray(config.stores) ? config.stores : [];
        const providerKey = config.provider || 'naver';
        const scriptUrl = config.scriptUrl || '';
        const globalPinUrl = config.globalPinUrl || '';

        const regionSelect = document.querySelector<HTMLSelectElement>('[data-store-region]');
        const districtSelect = document.querySelector<HTMLSelectElement>('[data-store-district]');
        const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-store-card]'));
        const emptyMsg = document.querySelector<HTMLElement>('[data-store-empty]');
        const mapNode = document.getElementById('store-map');

        if (!regionSelect || !districtSelect) return;

        let activeId = stores.length ? String(stores[0].id) : '';
        let storeMap: StoreMap | null = null;

        function unique(values: string[]): string[] {
            const seen: Record<string, true> = {};
            return values
                .filter((v) => { if (!v || seen[v]) return false; seen[v] = true; return true; })
                .sort((a, b) => a.localeCompare(b, 'ko'));
        }

        function syncDistrictOptions(): void {
            const sel = regionSelect!.value;
            const prev = districtSelect!.value;
            const list = unique(
                stores
                    .filter((s) => !sel || s.region === sel)
                    .map((s) => s.district),
            );
            districtSelect!.innerHTML = '';
            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = '시/군/구';
            districtSelect!.appendChild(placeholder);
            list.forEach((d) => {
                const o = document.createElement('option');
                o.value = d;
                o.textContent = d;
                districtSelect!.appendChild(o);
            });
            districtSelect!.value = list.includes(prev) ? prev : '';
        }

        function isVisible(s: StoreItem): boolean {
            return (!regionSelect!.value || s.region === regionSelect!.value) &&
                   (!districtSelect!.value || s.district === districtSelect!.value);
        }

        function visibleStores(): StoreItem[] {
            return stores.filter(isVisible);
        }

        function applyFilters(): void {
            const items = visibleStores();
            const visibleIds = new Set(items.map((s) => String(s.id)));

            cards.forEach((card) => {
                card.hidden = !visibleIds.has(card.getAttribute('data-store-id') || '');
            });
            if (emptyMsg) emptyMsg.hidden = items.length > 0;

            if (!visibleIds.has(activeId) && items.length) {
                activeId = String(items[0].id);
            }

            setActiveCard(activeId);

            if (storeMap) {
                storeMap.applyFilter(visibleIds);
            }
        }

        function setActiveCard(id: string): void {
            cards.forEach((card) => {
                card.classList.toggle('is-active', card.getAttribute('data-store-id') === id);
            });
        }

        cards.forEach((card) => {
            // MAP button: pan map to pin
            const mapBtn = card.querySelector<HTMLElement>('[data-store-map-btn]');
            if (mapBtn) {
                mapBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const id = card.getAttribute('data-store-id') || '';
                    activeId = id;
                    setActiveCard(id);
                    if (storeMap) storeMap.activateStore(id, true);
                });
            }

            // Card area click: activate card only, no map pan
            card.addEventListener('click', (e) => {
                if ((e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('[data-store-map-btn]')) return;
                const id = card.getAttribute('data-store-id') || '';
                activeId = id;
                setActiveCard(id);
                if (storeMap) storeMap.activateStore(id, false);
            });
            card.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                const id = card.getAttribute('data-store-id') || '';
                activeId = id;
                setActiveCard(id);
                if (storeMap) storeMap.activateStore(id, false);
            });
        });

        regionSelect.addEventListener('change', () => { syncDistrictOptions(); applyFilters(); });
        districtSelect.addEventListener('change', applyFilters);

        syncDistrictOptions();
        setActiveCard(activeId);

        if (mapNode && scriptUrl && stores.length) {
            storeMap = new StoreMap(
                providerKey,
                stores,
                globalPinUrl,
                (id) => { activeId = id; setActiveCard(id); },
            );
            storeMap.mount(scriptUrl, mapNode).catch(() => {
                mapNode.innerHTML = '<div class="map-status no-body-md --700">지도를 불러오지 못했습니다.</div>';
            });
        }
    },
};
